/**

 * Toast global — estilo Square-ish: carta flotante, ícono disco, borde whisper.

 * @see documentacion/design/prototypes/sucursal-flota-toasts-v1.html (opción B)

 */

import {

  Dimensions,

  Platform,

  Pressable,

  StyleSheet,

  Text,

  type TextStyle,

  type ViewStyle,

  View,

} from 'react-native';

import Toast from 'react-native-toast-message';

import type { ToastConfig } from 'react-native-toast-message';

import { Ionicons } from '@expo/vector-icons';

import type { ComponentProps } from 'react';

import { spacing, ticketColors, ticketFonts } from '../theme/public-api';



const VISIBILITY_MS = 3200;

/**
 * Tope duro de vida de un toast de producción. `autoHide` siempre va en true;
 * el clamp evita que un caller pase visibilityTime=Infinity/0 y deje un
 * mensaje clavado en pantalla.
 */
const MAX_VISIBILITY_MS = 10_000;

function clampVisibilityTime(visibilityTime?: number): number {
  const raw = visibilityTime ?? VISIBILITY_MS;
  if (!Number.isFinite(raw) || raw <= 0) return VISIBILITY_MS;
  return Math.min(raw, MAX_VISIBILITY_MS);
}

/** Fallback si un re-render pesado (p. ej. KDS sync) corta el timer de la lib. */
let toastAutoHideGeneration = 0;

function scheduleToastAutoHideFallback(generation: number, visibilityMs: number) {
  setTimeout(() => {
    if (generation === toastAutoHideGeneration) {
      Toast.hide();
    }
  }, visibilityMs + 80);
}

/** Cierra cualquier toast visible (logout, cambio de org/sucursal). */
export function hideAppToasts() {
  toastAutoHideGeneration += 1;
  Toast.hide();
}

const TOAST_CARD_MAX_WIDTH = 420;



function toastCardMaxWidth(): number {

  const windowWidth = Dimensions.get('window').width;

  return Math.min(Math.max(windowWidth - 24, 200), TOAST_CARD_MAX_WIDTH);

}



/** Offset superior respetando safe area + altura aprox. del shell header. */

export function toastTopOffset(topInset: number): number {

  return Math.max(topInset, spacing.sm) + 44;

}



type IconName = ComponentProps<typeof Ionicons>['name'];



type ToastToneStyle = {

  iconBg: string;

  iconName: IconName;

};



const toneStyles: Record<AppToastTone, ToastToneStyle> = {

  success: { iconBg: '#15803D', iconName: 'checkmark' },

  error: { iconBg: '#DC2626', iconName: 'alert' },

  info: { iconBg: ticketColors.gold, iconName: 'information-circle' },

};



const styles = StyleSheet.create({

  card: {

    alignSelf: 'center',

    flexDirection: 'row',

    alignItems: 'flex-start',

    gap: 10,

    flexGrow: 0,

    flexShrink: 0,

    borderRadius: 14,

    borderWidth: 1,

    borderColor: ticketColors.ruleSoft,

    backgroundColor: ticketColors.paper,

    paddingVertical: 12,

    paddingHorizontal: 14,

    ...(Platform.OS === 'web'

      ? ({

          boxShadow: '0 4px 16px rgba(15, 20, 18, 0.1), 0 1px 2px rgba(15, 20, 18, 0.04)',

        } as const)

      : {

          shadowColor: '#0F1412',

          shadowOffset: { width: 0, height: 4 },

          shadowOpacity: 0.1,

          shadowRadius: 12,

          elevation: 4,

        }),

  },

  iconDisc: {

    width: 28,

    height: 28,

    borderRadius: 14,

    alignItems: 'center',

    justifyContent: 'center',

    flexShrink: 0,

  },

  copy: {

    flexShrink: 1,

    minWidth: 0,

    paddingTop: 2,

  },

  text1: {

    color: ticketColors.ink,

    fontSize: 14,

    fontWeight: '700',

    lineHeight: 18,

    letterSpacing: -0.1,

    fontFamily: ticketFonts.sans,

  },

  text1Multiline: {

    flexShrink: 1,

  },

  text2: {

    color: ticketColors.ink2,

    fontSize: 13,

    lineHeight: 18,

    marginTop: 2,

    flexShrink: 1,

    fontFamily: ticketFonts.sans,

  },

});



type AppToastCardProps = {

  text1?: string;

  text2?: string;

  tone: AppToastTone;

  onPress?: () => void;

};



const webFitContent =

  Platform.OS === 'web' ? ({ width: 'fit-content' as unknown as ViewStyle['width'] } as ViewStyle) : null;



const AppToastCard = ({ text1, text2, tone, onPress }: AppToastCardProps) => {

  const compact = !text2;

  const toneStyle = toneStyles[tone];



  const content = (

    <View

      style={[

        styles.card,

        compact

          ? webFitContent

          : { width: toastCardMaxWidth(), maxWidth: toastCardMaxWidth() },

      ]}

    >

      <View style={[styles.iconDisc, { backgroundColor: toneStyle.iconBg }]}>

        <Ionicons name={toneStyle.iconName} size={16} color="#FFFFFF" />

      </View>

      <View style={styles.copy}>

        {!!text1 && (

          <Text

            numberOfLines={compact ? 1 : 3}

            style={[

              styles.text1,

              !compact ? styles.text1Multiline : null,

              compact && Platform.OS === 'web'

                ? ({ whiteSpace: 'nowrap' } as TextStyle)

                : null,

            ]}

          >

            {text1}

          </Text>

        )}

        {!!text2 && (

          <Text numberOfLines={4} style={styles.text2}>

            {text2}

          </Text>

        )}

      </View>

    </View>

  );

  if (!onPress) return content;

  return <Pressable onPress={onPress}>{content}</Pressable>;

};



export const appToastConfig: ToastConfig = {

  success: (props) => (

    <AppToastCard

      text1={props.text1}

      text2={props.text2}

      onPress={() => {

        props.onPress?.();

        props.hide();

      }}

      tone="success"

    />

  ),

  error: (props) => (

    <AppToastCard

      text1={props.text1}

      text2={props.text2}

      onPress={() => {

        props.onPress?.();

        props.hide();

      }}

      tone="error"

    />

  ),

  info: (props) => (

    <AppToastCard

      text1={props.text1}

      text2={props.text2}

      onPress={() => {

        props.onPress?.();

        props.hide();

      }}

      tone="info"

    />

  ),

};



export function showSuccess(

  text1: string,

  text2?: string,

  visibilityTime?: number,

  onPress?: () => void,

) {
  const visibilityMs = clampVisibilityTime(visibilityTime);
  const generation = ++toastAutoHideGeneration;

  Toast.show({
    type: 'success',
    text1,
    text2: text2 ?? undefined,
    visibilityTime: visibilityMs,
    autoHide: true,
    position: 'top',
    onPress: () => {
      onPress?.();
      toastAutoHideGeneration += 1;
      Toast.hide();
    },
  });

  scheduleToastAutoHideFallback(generation, visibilityMs);
}



/**

 * Toast.show apunta al último `<Toast />` montado. Los modales nativos montan un host

 * local (taparía el global); si cerrás el modal en el mismo tick, ese host se va y el

 * toast nunca se ve. Diferir al próximo macrotask deja que React desmonte el host del

 * modal y el toast global (o el que quede activo) reciba el mensaje.

 */

function showAfterModalClose(show: () => void) {

  // OBS-10: 50ms da tiempo a que React desmonte el host del modal antes del toast

  setTimeout(show, 50);

}



export function showSuccessAfterModalClose(

  text1: string,

  text2?: string,

  visibilityTime?: number,

  onPress?: () => void,

) {

  showAfterModalClose(() => showSuccess(text1, text2, visibilityTime, onPress));

}



export function showErrorAfterModalClose(text1: string, text2?: string) {

  showAfterModalClose(() => showError(text1, text2));

}



function errorVisibilityMs(text1: string, text2?: string): number {

  const len = text1.length + (text2?.length ?? 0);

  return Math.min(7000, Math.max(VISIBILITY_MS, 4000 + Math.floor(len * 20)));

}



export type ShowErrorReportOptions = {

  /**

   * Mandar a Sentry (fallo inesperado que el usuario ya vio).

   * No usar para validación / sin permiso.

   */

  report?: boolean;

  module?: string;

  op?: string;

  error?: unknown;

};



export function showError(text1: string, text2?: string, options?: ShowErrorReportOptions) {

  if (options?.report) {

    const { reportUserVisibleFailure } = require('../sentry/sentryUser') as typeof import('../sentry/sentryUser');

    reportUserVisibleFailure({

      module: options.module ?? 'app',

      op: options.op ?? 'toast_error',

      error: options.error ?? new Error([text1, text2].filter(Boolean).join(': ')),

      title: text1,

      detail: text2,

    });

  }

  const visibilityMs = clampVisibilityTime(errorVisibilityMs(text1, text2));
  const generation = ++toastAutoHideGeneration;

  Toast.show({
    type: 'error',
    text1,
    text2: text2 ?? undefined,
    visibilityTime: visibilityMs,
    autoHide: true,
    position: 'top',
    onPress: () => {
      toastAutoHideGeneration += 1;
      Toast.hide();
    },
  });

  scheduleToastAutoHideFallback(generation, visibilityMs);
}



export function showInfo(text1: string, text2?: string, visibilityTime: number = VISIBILITY_MS) {
  const visibilityMs = clampVisibilityTime(visibilityTime);
  const generation = ++toastAutoHideGeneration;

  Toast.show({
    type: 'info',
    text1,
    text2: text2 ?? undefined,
    visibilityTime: visibilityMs,
    autoHide: true,
    position: 'top',
    onPress: () => {
      toastAutoHideGeneration += 1;
      Toast.hide();
    },
  });

  scheduleToastAutoHideFallback(generation, visibilityMs);
}



export type AppToastTone = 'success' | 'error' | 'info';



/** Banner con el mismo look que el toast global (visible dentro de Modal nativo). */

export function InlineAppToast({

  tone,

  text1,

  text2,

}: {

  tone: AppToastTone;

  text1: string;

  text2?: string;

}) {

  return <AppToastCard text1={text1} text2={text2} tone={tone} />;

}


